import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import type { AggregatedMetricPanelConfig } from '@src/types/LayoutConfig';
import { useCallback, useEffect } from 'react';
import { Panel } from '../../../../Layout/Components/Panel';
import type { LayoutPanelProps } from '../../../types';
import { resolveMetricDisplayName, useMetricDisplayNameMap } from '../core';
import {
  AggregationMethod,
  formatAggregatedMetricDefaultTitle,
  type AggregatedMetricModuleConfig,
} from './AggregatedMetric.types';
import { AggregatedMetricModule } from './AggregatedMetricModule';

/**
 * Defaults baked into the `'aggregatedAssetMetric'` panel type. `defaultMetric` is the
 * starting metric for new panels; users can pick a different one in the config view.
 * The y-axis / tooltip formatter is resolved per-render from the active metric in
 * `AggregatedMetricModule`, so it doesn't live in this config.
 */
const AGGREGATED_ASSET_METRIC_CONFIG: AggregatedMetricModuleConfig = {
  defaultMetric: 'CapMrktEstUSD',
  defaultCmAssetIds: ['btc', 'eth', 'sol'],
  defaultAggregationMethod: AggregationMethod.SUM,
  frequency: '1d',
  label: 'Aggregated Asset Metric',
};

export function AggregatedMetricPanel({ api, params }: LayoutPanelProps<AggregatedMetricPanelConfig>) {
  const { enableSignals } = useFeatureFlag();

  const handleParamsChange = useCallback(
    (partial: Partial<AggregatedMetricPanelConfig>) => api.updateParameters(partial),
    [api]
  );

  // Mirror the metric-derived default title onto the dockview tab. Skipped when
  // `params.customTitle` is true — that flag is set by both the tab context menu rename
  // (via DefaultTabComponent) and the config view's Title field, so in either case dockview
  // already holds the correct title and we must not overwrite it on metric changes.
  // The `api.title` guard avoids redundant dockview commits when the title already matches.
  const effectiveMetric = params.metric ?? AGGREGATED_ASSET_METRIC_CONFIG.defaultMetric;
  const { displayNameByMetricId } = useMetricDisplayNameMap();
  const effectiveTitle = formatAggregatedMetricDefaultTitle(
    resolveMetricDisplayName(effectiveMetric, displayNameByMetricId)
  );
  useEffect(() => {
    if (params.customTitle) {
      return;
    }
    if (api.title !== effectiveTitle) {
      api.setTitle(effectiveTitle);
    }
  }, [api, effectiveTitle, params.customTitle]);

  // Title changes from the config view: write directly to the dockview tab (same as the
  // tab context menu rename in DefaultTabComponent) and set `customTitle` accordingly.
  // Clearing the field hands back control to the effect above.
  const handleTitleChange = useCallback(
    (next: string) => {
      const trimmed = next.trim();
      if (trimmed) {
        api.setTitle(trimmed);
        api.updateParameters({ customTitle: true });
      } else {
        api.updateParameters({ customTitle: false });
      }
    },
    [api]
  );

  if (!enableSignals) {
    return null;
  }

  return (
    <Panel
      panelApi={api}
      position="relative"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      data-testid="aggregated-asset-metric-panel"
    >
      <AggregatedMetricModule
        config={AGGREGATED_ASSET_METRIC_CONFIG}
        params={params}
        onParamsChange={handleParamsChange}
        title={params.customTitle ? (api.title ?? '') : ''}
        onTitleChange={handleTitleChange}
      />
    </Panel>
  );
}
